import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CiSearch } from "react-icons/ci";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import CreatePaymentLinkDialog from "./CreatePaymentLinkDialog";
import PaymentLinksTab from "./PaymentLinksTab";
import {
  useGetOnlinePaymentsQuery,
  useResendOnlinePaymentEmailMutation,
  useRefundOnlinePaymentMutation,
} from "../../Api/attoDeskApi";
import {
  IOnlinePayment,
  IOnlinePaymentTab,
} from "../../Api/Interface/api.interface";
import Pagination from "../../Components/Pagination";
import useDebounce from "../../Components/SearchDebonce/useDebounce";
import { formatCurrency } from "../../Core/utils/money";
import { useNotifier } from "../../Core/Notifier";

const BRAND = "#2f3891";
const ROWS_PER_PAGE = 25;

/**
 * Card payments taken through the Easy Pay portal.
 *
 * The tab split is a correctness requirement, not a convenience. "Declined"
 * means Bambora definitively refused the card and no money moved. A payment
 * whose outcome could not be established — a timeout, or an approval whose
 * bookkeeping failed — is NOT a decline, and listing it as one sends staff to
 * tell a customer their payment failed when it may well have succeeded. Those
 * live in their own tab, each keeping the specific status the backend assigned.
 */
const TABS: { key: IOnlinePaymentTab; label: string; hint: string }[] = [
  {
    key: "successful",
    label: "Successful Transactions",
    hint: "Payments that reached a completed state and are fully recorded.",
  },
  {
    key: "declined",
    label: "Declined Transactions",
    hint: "Payments the gateway definitively refused. No money moved.",
  },
  {
    key: "reconciliation",
    label: "Needs Reconciliation",
    hint: "Outcomes that could not be confirmed. Do NOT treat these as declines — money may have moved.",
  },
];

const thSx = {
  fontWeight: 700,
  fontSize: 11,
  color: "#64748b",
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #e2e8f0",
  bgcolor: "#f8fafc",
  whiteSpace: "nowrap" as const,
};

const tdSx = { py: 1.4, px: 2, borderBottom: "1px solid #f1f5f9", fontSize: 13 };

const STATUS_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  completed: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  declined: { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
  // Amber, not red: these are unresolved, not failed.
  gateway_approved: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  reconciliation_required: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  charging: { bg: "#e0e7ff", color: "#3730a3", border: "#c7d2fe" },
  configuration_error: { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" },
};

const StatusChip = ({ payment }: { payment: IOnlinePayment }) => {
  const style = STATUS_STYLES[payment.status] ?? STATUS_STYLES.configuration_error;

  return (
    <Chip
      label={payment.status_label}
      size="small"
      sx={{
        bgcolor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        fontWeight: 700,
        fontSize: 10,
        height: 20,
      }}
    />
  );
};

const formatDate = (value: string | null) =>
  value ? dayjs(value).format("MMM D, YYYY h:mm A") : "—";

/**
 * "Voided" or "Refunded" — kept visually distinct from the status chip because
 * it answers a different question. Status says what the gateway did with the
 * charge; this says the money has since gone back.
 */
const ReversalChip = ({
  reversal,
}: {
  reversal: NonNullable<IOnlinePayment["reversal"]>;
}) => (
  <Chip
    label={reversal.label}
    size="small"
    sx={{
      bgcolor: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
      fontWeight: 700,
      fontSize: 10,
      height: 20,
    }}
  />
);

const OnlinePaymentsPage: React.FC = () => {
  // "links" is not a transaction tab — it lists payment requests, which have
  // their own endpoint. The transaction query is skipped while it is selected.
  const [tab, setTab] = useState<IOnlinePaymentTab | "links">("successful");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<IOnlinePayment | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 350);

  const { data, isFetching, isError, refetch } = useGetOnlinePaymentsQuery({
    // "links" is never sent as a tab value — the API only knows the three
    // transaction tabs. The query keeps running while the Payment Links tab is
    // open so the header count and the tab badges stay populated behind it.
    tab: tab === "links" ? "successful" : tab,
    search: debouncedSearch,
    date_from: dateFrom,
    date_to: dateTo,
    amount_min: amountMin,
    amount_max: amountMax,
    // The API is 1-indexed; the shared Pagination component is 0-indexed.
    page: page + 1,
    per_page: ROWS_PER_PAGE,
  });

  const rows: IOnlinePayment[] = useMemo(() => data?.data ?? [], [data]);
  const total = data?.meta?.total ?? 0;
  const counts = data?.meta?.counts;

  const hasFilters = Boolean(
    search || dateFrom || dateTo || amountMin || amountMax
  );

  // Any filter change invalidates the current page number — page 4 of the old
  // result set is meaningless against the new one.
  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setAmountMin("");
    setAmountMax("");
    setPage(0);
  };

  const activeTab = TABS.find((t) => t.key === tab)!;

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg,${BRAND},${BRAND})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
              Online Payments
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
              {total} transaction{total !== 1 ? "s" : ""}
              {hasFilters ? " matching your filters" : ""}
            </Typography>
          </Box>
        </Box>

        {/* A payment request, not an invoice — it deliberately sits here rather
            than in the invoice screens. */}
        <Button
          variant="contained"
          startIcon={<AddLinkOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: BRAND, fontWeight: 600 }}
        >
          Create Payment Link
        </Button>
      </Box>

      <CreatePaymentLinkDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        // A paid link shows up as an ordinary payment here, so the list is
        // refreshed once one is created.
        onCreated={() => refetch()}
      />

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, next) => {
            setTab(next);
            setPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid #e2e8f0",
            px: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              minHeight: 48,
              color: "#64748b",
            },
            "& .Mui-selected": { color: `${BRAND} !important` },
            "& .MuiTabs-indicator": { backgroundColor: BRAND, height: 3 },
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={
                <Tooltip title={t.hint} arrow>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {t.key === "reconciliation" && (
                      <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: "#d97706" }} />
                    )}
                    {t.label}
                    {counts && (
                      <Chip
                        label={counts[t.key] ?? 0}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: tab === t.key ? "#eef2ff" : "#f1f5f9",
                          color: tab === t.key ? BRAND : "#64748b",
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              }
            />
          ))}
          {/* Payment requests, not transactions — so it carries no count from
              the transaction query and owns its own filters and paging. */}
          <Tab
            value="links"
            label={
              <Tooltip title="Payment requests staff created, and whether they have been paid." arrow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <AddLinkOutlinedIcon sx={{ fontSize: 15 }} />
                  Payment Links
                </Box>
              </Tooltip>
            }
          />
        </Tabs>

        {tab === "links" ? (
          <PaymentLinksTab />
        ) : (
        <>
        {/* ── Filters ───────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            px: 2,
            py: 2,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <TextField
            size="small"
            placeholder="Customer, email, transaction ID, reference…"
            value={search}
            onChange={(e) => changeFilter(setSearch)(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ color: "#94a3b8", fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 13, bgcolor: "#fff", minWidth: 300 },
            }}
          />
          <TextField
            size="small"
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => changeFilter(setDateFrom)(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-root": { borderRadius: 2, fontSize: 13 } }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => changeFilter(setDateTo)(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-root": { borderRadius: 2, fontSize: 13 } }}
          />
          <TextField
            size="small"
            type="number"
            label="Min $"
            value={amountMin}
            onChange={(e) => changeFilter(setAmountMin)(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 110, "& .MuiInputBase-root": { borderRadius: 2, fontSize: 13 } }}
          />
          <TextField
            size="small"
            type="number"
            label="Max $"
            value={amountMax}
            onChange={(e) => changeFilter(setAmountMax)(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 110, "& .MuiInputBase-root": { borderRadius: 2, fontSize: 13 } }}
          />
          {hasFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: "none", fontSize: 12, color: "#64748b" }}
            >
              Clear
            </Button>
          )}
        </Box>

        {/* ── Reconciliation warning ────────────────────────────────────────── */}
        {tab === "reconciliation" && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              px: 2,
              py: 1.5,
              bgcolor: "#fffbeb",
              borderBottom: "1px solid #fde68a",
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: "#d97706", mt: 0.1 }} />
            <Typography sx={{ fontSize: 12, color: "#92400e" }}>
              These are <strong>not declines</strong>. The gateway outcome could not be
              confirmed, so money may have moved. Check Bambora before telling a customer
              their payment failed, and never ask them to pay again without confirming first.
            </Typography>
          </Box>
        )}

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Date</TableCell>
                  <TableCell sx={thSx}>Customer</TableCell>
                  <TableCell sx={thSx}>Email</TableCell>
                  <TableCell sx={thSx}>Reference / Invoice</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Amount</TableCell>
                  <TableCell sx={thSx}>Transaction ID</TableCell>
                  <TableCell sx={thSx}>Method</TableCell>
                  {tab !== "successful" && <TableCell sx={thSx}>Reason</TableCell>}
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tab === "successful" ? 9 : 10}>
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <CreditCardOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                        <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                          {isError
                            ? "Error fetching online payments"
                            : hasFilters
                            ? "No transactions match your filters"
                            : `No ${activeTab.label.toLowerCase()} yet`}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fafcff" } }}>
                      <TableCell sx={{ ...tdSx, whiteSpace: "nowrap", color: "#475569" }}>
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {row.customer_name || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                          {row.source_label}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: "#475569" }}>
                        {row.customer_email || "—"}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: "#475569" }}>
                        {row.reference || "—"}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {row.amount === null ? "—" : `${formatCurrency(row.amount)} ${row.currency}`}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, fontFamily: "monospace", fontSize: 12, color: "#475569" }}>
                        {row.transaction_id || "—"}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: "#475569" }}>
                        {row.payment_method || "—"}
                      </TableCell>
                      {tab !== "successful" && (
                        <TableCell sx={{ ...tdSx, color: "#64748b", maxWidth: 220 }}>
                          <Tooltip title={row.decline_reason || ""} arrow>
                            <Typography
                              sx={{
                                fontSize: 12,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 220,
                              }}
                            >
                              {row.decline_reason || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap" }}>
                          <StatusChip payment={row} />
                          {/* A reversed payment stays "Completed" — the charge
                              did happen — so this is what says the money went
                              back, and it must be visible without opening the row. */}
                          {row.reversal && <ReversalChip reversal={row.reversal} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                        <Tooltip title="View details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setSelected(row)}
                            sx={{
                              color: BRAND,
                              bgcolor: "#eef2ff",
                              borderRadius: 1.5,
                              width: 30,
                              height: 30,
                              "&:hover": { bgcolor: "#e0e7ff" },
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Pagination ────────────────────────────────────────────────────── */}
        {total > ROWS_PER_PAGE && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Pagination
              count={total}
              page={page}
              rowsPerPage={ROWS_PER_PAGE}
              onPageChange={(_, p) => setPage(p)}
            />
          </Box>
        )}
        </>
        )}
      </Box>

      {/* ── Detail dialog ───────────────────────────────────────────────────── */}
      <PaymentDetailDialog payment={selected} onClose={() => setSelected(null)} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.9 }}>
    <Typography sx={{ fontSize: 12, color: "#94a3b8", width: 150, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, color: "#1e293b", fontWeight: 500, wordBreak: "break-word" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const PaymentDetailDialog = ({
  payment,
  onClose,
}: {
  payment: IOnlinePayment | null;
  onClose: () => void;
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [resend, { isLoading: resending }] = useResendOnlinePaymentEmailMutation();
  const [refund, { isLoading: refunding }] = useRefundOnlinePaymentMutation();
  // Which button is in flight, so only that one shows a spinner.
  const [pending, setPending] = useState<"customer" | "admin" | null>(null);
  const [confirmRefund, setConfirmRefund] = useState(false);

  // A re-send is only meaningful once the payment is settled and recorded —
  // the same rule the endpoint enforces, mirrored here so the buttons are not
  // offered on a row that would be rejected.
  const canResend = payment?.status === "completed";
  const hasCustomerEmail = Boolean(payment?.customer_email);

  // Same gate, plus "not already reversed". Anything unresolved
  // (reconciliation_required, charging, gateway_approved) is deliberately
  // excluded: whether money moved is exactly what is unknown there, and that
  // has to be settled against the Bambora portal, not guessed at from here.
  const canRefund = payment?.status === "completed" && !payment?.reversal;

  const handleRefund = async () => {
    if (!payment) return;
    try {
      const res: any = await refund({ id: payment.id }).unwrap();
      showMessage(res?.message ?? "Payment reversed", "success");
      setConfirmRefund(false);
      // Closed rather than re-rendered: the row in hand is now stale, and the
      // list refetches from the invalidated tag.
      onClose();
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "The reversal failed");
    }
  };

  const handleResend = async (recipient: "customer" | "admin") => {
    if (!payment) return;
    setPending(recipient);
    try {
      const res: any = await resend({ id: payment.id, recipient }).unwrap();
      showMessage(res?.message ?? "Email sent", "success");
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "The email could not be sent");
    } finally {
      setPending(null);
    }
  };

  return (
  <>
  <Dialog open={Boolean(payment)} onClose={onClose} maxWidth="sm" fullWidth>
    {payment && (
      <>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            Transaction Details
            <StatusChip payment={payment} />
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DetailRow label="Date" value={formatDate(payment.date)} />
          <DetailRow label="Type" value={payment.source_label} />
          <DetailRow label="Customer" value={payment.customer_name} />
          <DetailRow label="Email" value={payment.customer_email} />
          <DetailRow label="Phone" value={payment.customer_phone} />
          <DetailRow label="Reference / Invoice" value={payment.reference} />
          <DetailRow
            label="Amount"
            value={
              payment.amount === null
                ? "—"
                : `${formatCurrency(payment.amount)} ${payment.currency}`
            }
          />
          <DetailRow label="Transaction ID" value={payment.transaction_id} />
          <DetailRow label="Payment Method" value={payment.payment_method} />
          {payment.status !== "completed" && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <DetailRow label="Gateway Message" value={payment.decline_reason} />
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                Internal only — this text is never sent to the customer.
              </Typography>
            </>
          )}
          {payment.reversal && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#c2410c" }}>
                  Reversal
                </Typography>
                <ReversalChip reversal={payment.reversal} />
              </Box>
              <DetailRow
                label="Amount Reversed"
                value={
                  payment.reversal.amount === null
                    ? "—"
                    : `${formatCurrency(payment.reversal.amount)} ${payment.currency}`
                }
              />
              <DetailRow label="Reversal Transaction ID" value={payment.reversal.transaction_id} />
              <DetailRow label="Reversed On" value={formatDate(payment.reversal.reversed_at)} />
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                {payment.reversal.type === "void"
                  ? "Voided before the daily cut-off — usually never appears on the customer's statement."
                  : "Refunded after settlement — typically reaches the customer's account in 5–10 business days."}
              </Typography>
            </>
          )}
        </DialogContent>
        {canResend && (
          <>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: "flex-start", flexWrap: "wrap" }}>
              {canRefund && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={refunding}
                  onClick={() => setConfirmRefund(true)}
                  startIcon={<UndoOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none", borderRadius: 1.5, mr: "auto" }}
                >
                  Refund
                </Button>
              )}
              <Tooltip
                title={hasCustomerEmail ? "" : "No customer email address on this payment"}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!hasCustomerEmail || resending}
                    onClick={() => handleResend("customer")}
                    startIcon={
                      pending === "customer"
                        ? <CircularProgress size={14} />
                        : <ForwardToInboxOutlinedIcon sx={{ fontSize: 16 }} />
                    }
                    sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: BRAND }}
                  >
                    Resend Receipt
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                disabled={resending}
                onClick={() => handleResend("admin")}
                startIcon={
                  pending === "admin"
                    ? <CircularProgress size={14} />
                    : <ForwardToInboxOutlinedIcon sx={{ fontSize: 16 }} />
                }
                sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}
              >
                Resend Admin Notification
              </Button>
            </DialogActions>
          </>
        )}
      </>
    )}
  </Dialog>

  {/* Separate confirmation, because this one moves real money and cannot be
      undone from this screen. It states the amount rather than asking a bare
      "are you sure?". */}
  <Dialog open={confirmRefund} onClose={() => setConfirmRefund(false)} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
      Refund this payment?
    </DialogTitle>
    <DialogContent>
      <Typography sx={{ fontSize: 13, color: "#475569" }}>
        {payment?.amount === null || payment === null
          ? "This payment will be returned to the customer's card."
          : `${formatCurrency(payment.amount)} ${payment.currency} will be returned to ${
              payment.customer_name || "the customer"
            }'s card.`}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1.5 }}>
        Before the daily cut-off this is processed as a void and usually never reaches the
        customer's statement; afterwards it is a refund and takes 5–10 business days. The
        gateway decides which applies.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
      <Button
        variant="outlined"
        onClick={() => setConfirmRefund(false)}
        disabled={refunding}
        sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        color="warning"
        onClick={handleRefund}
        disabled={refunding}
        startIcon={refunding ? <CircularProgress size={14} color="inherit" /> : undefined}
        sx={{ textTransform: "none", borderRadius: 1.5 }}
      >
        {refunding ? "Processing…" : "Refund"}
      </Button>
    </DialogActions>
  </Dialog>
  </>
  );
};

export default OnlinePaymentsPage;
